import { t } from '@lingui/core/macro';
import { PaymentProvider, PaymentSessionOutputRecord } from '@navio/nsdk';
import { noddiToast } from 'noddi-ui-web';
import { log } from 'noddi-web';
import type { NavigateOptions, To } from 'react-router-dom';
import { StripeEmbeddedSession, saveStripeEmbeddedSession } from './stripeEmbeddedSession';

type NavigateFn = (to: To, options?: NavigateOptions) => void;

export const PAYMENT_SESSION_STATE_KEY = 'stripeEmbeddedSession';

/**
 * Route a provider-agnostic payment session to the embedded pay route.
 *
 * Both providers render inside our page (no redirect) and navigate to the same
 * route keyed by `sessionId`. Dintero re-embeds from that id alone; Stripe needs
 * its Embedded Checkout `clientSecret` + publishable key + connected account. We
 * hand that to the pay route via router `state` (survives fresh tabs), plus a
 * best-effort `sessionStorage` copy so a hard refresh can rebuild it.
 *
 * A Stripe session with no embed data (misconfigured / not fully onboarded
 * account) cannot render the widget — navigating there would strand the customer
 * on an "expired session" dead-end for an order that already exists. In that case
 * we surface the error and do not navigate. Returns whether navigation happened.
 */
export const routePaymentSession = ({
  session,
  navigate,
  getEmbeddedPath,
  navigateOptions
}: {
  session: PaymentSessionOutputRecord;
  navigate: NavigateFn;
  getEmbeddedPath: (session: PaymentSessionOutputRecord) => string;
  navigateOptions?: NavigateOptions;
}): boolean => {
  const isStripe = session.provider === PaymentProvider.STRIPE;
  const stripeSession: StripeEmbeddedSession | null =
    isStripe && session.clientSecret && session.publishableKey
      ? {
          clientSecret: session.clientSecret,
          publishableKey: session.publishableKey,
          stripeAccountId: session.stripeAccountId ?? null,
          countryCode: session.countryCode
        }
      : null;

  if (isStripe && !stripeSession) {
    log({
      value: 'Stripe payment session missing client secret / publishable key',
      severity: 'error',
      cause: new Error(`Payment session ${session.sessionId} has no Stripe embed data`)
    });
    noddiToast.error(t`Error creating booking - please contact support if the problem persists`);
    return false;
  }

  if (stripeSession) {
    saveStripeEmbeddedSession(session.sessionId, stripeSession);
  }

  navigate(getEmbeddedPath(session), {
    ...navigateOptions,
    state: stripeSession ? { [PAYMENT_SESSION_STATE_KEY]: stripeSession } : navigateOptions?.state
  });
  return true;
};
